import { useMemo, useCallback } from 'react';
import { PhoneNumbersV1 } from '@frontend/api-phone-numbers';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { useAppFlagStore } from '@frontend/shared';
import { formatProtobufPhone } from '../utils';
import { useInboxLocationIds } from './use-inbox-location-ids';

type CheckPhoneAccessFunction = (phoneNumber: string) => boolean;

/**
 * Hook to check if a user can access specific phone numbers based on their permissions.
 *
 * This hook validates phone number access using the same logic as useLocationPhones,
 * checking against user-accessible phone numbers when the modern phone number feature
 * flag is enabled.
 *
 * @param locationIds - Optional location IDs to scope the check. If not provided, uses current selection.
 *
 * @returns A function to check phone access for a given phone number.
 */
export const useCheckUserLocationPhoneAccess = (providedLocationIds: string[] = []): CheckPhoneAccessFunction => {
  const { getFeatureFlagValue } = useAppFlagStore();
  const locationIds = useInboxLocationIds({ locationIds: providedLocationIds });

  const isLocationPhonesEnabled = getFeatureFlagValue({
    locationIds,
    flagName: 'phone-numbers-name-id',
    strategy: 'ALL',
  });

  const isPrivateCommsEnabled = getFeatureFlagValue({
    locationIds,
    flagName: 'apply-phone-number-user-access-restriction',
    strategy: 'ALL',
  });

  const accessiblePhonesQuery = PhoneNumbersV1.Queries.useListUserAccessiblePhoneNumbersQuery({
    request: {
      locationIds,
    },
    options: {
      enabled: isLocationPhonesEnabled && isPrivateCommsEnabled && locationIds.length > 0,
    },
  });

  // Memoize the accessible phone numbers for performance
  const accessiblePhoneNumbers = useMemo(() => {
    if (!accessiblePhonesQuery.data?.phoneNumbers) {
      return new Set<string>();
    }

    return new Set(
      accessiblePhonesQuery.data.phoneNumbers
        .map((phoneNumberData) => {
          const formattedPhone = formatProtobufPhone(phoneNumberData.phoneNumber);
          return formattedPhone ? formatPhoneNumberE164(formattedPhone) : undefined;
        })
        .filter(Boolean)
    );
  }, [accessiblePhonesQuery.data?.phoneNumbers]);

  const checkPhoneAccess = useCallback(
    (phoneNumber: string): boolean => {
      // If no location IDs are available, assume accessible (backward compatibility/initialization)
      if (locationIds.length === 0) {
        return true;
      }

      // If feature flag is disabled, fall back to legacy access logic (assume accessible)
      if (!isLocationPhonesEnabled || !isPrivateCommsEnabled) {
        return true;
      }

      // Normalize the phone number and check against accessible numbers
      const normalizedPhoneNumber = formatPhoneNumberE164(phoneNumber);
      return accessiblePhoneNumbers.has(normalizedPhoneNumber);
    },
    [isLocationPhonesEnabled, isPrivateCommsEnabled, accessiblePhoneNumbers, locationIds.length]
  );

  return checkPhoneAccess;
};
